import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { AdditiveBlending, BufferAttribute, BufferGeometry } from 'three';
import { seededRandom } from './random.js';
import { softSpriteTexture } from './softSprite.js';
import { useCelestial } from './CelestialProvider.jsx';

// Fully procedural sky (NASA Deep Star Maps ships EXR-only — documented in
// LICENSES.assets.md): a far shell of fine stars + a near parallax layer.
// Zero asset weight; the JWST photo planes carry the color moments.
function makeShell({ count, rMin, rMax, seed, ySquash = 1 }) {
  const random = seededRandom(seed);
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = rMin + random() * (rMax - rMin);
    const theta = random() * Math.PI * 2;
    const phi = Math.acos(2 * random() - 1);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi) * ySquash;
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    // temperature variation: white → warm → faint blue
    const warm = random();
    const dim = 0.35 + random() * 0.65;
    colors[i * 3] = dim * (0.82 + warm * 0.18);
    colors[i * 3 + 1] = dim * (0.84 + warm * 0.08);
    colors[i * 3 + 2] = dim * (0.88 + (1 - warm) * 0.12);
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(positions, 3));
  geo.setAttribute('color', new BufferAttribute(colors, 3));
  return geo;
}

export function Starfield() {
  const { perfTier, seeds } = useCelestial();
  const groupRef = useRef();
  const nearRef = useRef();
  const { camera } = useThree();
  const sprite = softSpriteTexture({ size: 64 });

  const farGeometry = useMemo(
    () => makeShell({ count: Math.max(2400, perfTier.particles * 2), rMin: 60, rMax: 110, seed: seeds.galaxy * 3 }),
    [perfTier.particles, seeds.galaxy],
  );
  const nearGeometry = useMemo(
    () => makeShell({ count: perfTier.particles, rMin: 12, rMax: 40, seed: seeds.galaxy * 7, ySquash: 0.7 }),
    [perfTier.particles, seeds.galaxy],
  );

  useFrame((state) => {
    if (nearRef.current) nearRef.current.rotation.y = state.clock.elapsedTime * 0.004;
    // infinite-sky trick: the shells ride with the camera (journey travels far)
    if (groupRef.current) groupRef.current.position.copy(camera.position);
  });

  return (
    <group ref={groupRef}>
      <points geometry={farGeometry} frustumCulled={false}>
        <pointsMaterial map={sprite} size={0.22} transparent opacity={0.85} depthWrite={false} blending={AdditiveBlending} vertexColors />
      </points>
      <points ref={nearRef} geometry={nearGeometry}>
        <pointsMaterial map={sprite} size={0.06} sizeAttenuation transparent opacity={0.7} depthWrite={false} blending={AdditiveBlending} vertexColors />
      </points>
    </group>
  );
}
